import _ from 'lodash';
import { fetchUserProfileData } from '../api/signUpMutation';
import {
  IAuthorRequirementOptions,
  ISubmissionContext
} from '@publishing-engine/domain/models';
import { useQuery } from '@tanstack/react-query';
import { camelToPhrase } from '@publishing-engine/domain/utils';

const getLabelAndValue = (fieldName: string, userData: any) => {
  const authorData = userData?.data;

  switch (fieldName as IAuthorRequirementOptions) {
    case 'orcid':
      return {
        label: 'ORCID iD',
        value: authorData?.institutionalInformation?.orcid
      };
    case 'affiliation':
      return {
        label: 'Affiliation',
        value: authorData?.affiliationDetails
      };
    default:
      return {
        label: camelToPhrase(fieldName),
        value: _.get(authorData, fieldName)
      };
  }
};

export const useAuthorRequiredMissingFields = (
  config: ISubmissionContext['config']['authorRequirements'] = {}
) => {
  const requiredFields = _.pickBy(config, Boolean);
  const hasRequiredAuthorFields = !_.isEmpty(requiredFields);
  const fieldNames = _.keys(requiredFields);

  const { data: userData, isLoading: isUserDataLoading } = useQuery({
    queryKey: ['user-profile-data'],
    queryFn: () => (hasRequiredAuthorFields ? fetchUserProfileData() : null)
  });

  const { isAuthorInfoMissing, missingFieldNames } = fieldNames.reduce(
    (
      acc: {
        isAuthorInfoMissing: boolean;
        missingFieldNames: string[];
      },
      fieldName
    ) => {
      const result = getLabelAndValue(fieldName, userData);

      if (!result.value)
        return {
          ...acc,
          isAuthorInfoMissing: true,
          missingFieldNames: [...acc.missingFieldNames, result.label]
        };

      return acc;
    },
    { isAuthorInfoMissing: false, missingFieldNames: [] }
  );

  return {
    isUserDataLoading,
    isAuthorInfoMissing,
    missingFieldNames,
    userData
  };
};
